import * as s from '~js/utils/selectors';
import * as a from '~js/utils/attributes';
import * as c from '~js/utils/classes';
import {
  flickitySmoothScrolling,
} from '~js/utils/utils';

const Flickity = themeVendor.Flickity

export class Slider {
  constructor(section) {
    this.container = section.container;
    this.header = document.querySelector(s.selectors$3.header);
    this.flkty = null;
    this.resizeEvent = () => {
      this.flkty.resize();
    };

    this.initSlider();
    this.bindScrollButton();
  }

  initSlider() {
    const slidesCount = this.container.querySelectorAll(s.selectors$3.item).length;
    const duration = parseInt(this.container.dataset.duration);
    const pageDots = this.container.dataset.pageDots === 'true' && slidesCount > 1;
    const prevNextButtons = this.container.dataset.navArrows === 'true' && slidesCount > 1;
    let autoplay = this.container.dataset.autoplay === 'true';

    if (autoplay) {
      autoplay = duration;
    }

    if (slidesCount > 1) {
      this.flkty = new Flickity(this.container, {
        fade: true,
        cellSelector: s.selectors$3.item,
        autoPlay: autoplay,
        wrapAround: true,
        adaptiveHeight: true,
        setGallerySize: true,
        imagesLoaded: true,
        pageDots: pageDots,
        prevNextButtons: prevNextButtons,
        on: {
          ready: () => {
            const currentStyle = this.container
              .querySelector(`${s.selectors$3.item}[${a.attributes$2.slidePosition}="1"]`)
              .getAttribute(a.attributes$2.style);
            this.container.setAttribute(a.attributes$2.currentStyle, currentStyle);
            requestAnimationFrame(this.resizeEvent);
            document.addEventListener('theme:vars', this.resizeEvent); // Update slideshow height after height vars init

            this.handleFirstSlideAnimation(true);
          },
          change: (index) => {
            const currentSlide = this.flkty.selectedElement;
            const currentStyle = currentSlide.getAttribute(a.attributes$2.style);

            this.container.setAttribute(a.attributes$2.currentStyle, currentStyle);
            this.handleFirstSlideAnimation(false);

            this.flkty.cells.forEach((slide, i) => {
              slide.element.querySelectorAll(s.selectors$3.links).forEach((link) => {
                link.setAttribute(a.attributes$2.tabIndex, i === index ? '0' : '-1');
              });
            });
          },
        },
      });

      // iOS smooth scrolling fix
      flickitySmoothScrolling(this.container);
    } else if (slidesCount === 1) {
      const currentStyle = this.container.querySelector(s.selectors$3.item).getAttribute(a.attributes$2.style);
      this.container.setAttribute(a.attributes$2.currentStyle, currentStyle);
    }
  }

  /**
   * Handles image animation in first slide that would be triggered on page load
   *  - uses a fallback class modifier for sliders that contain a first slide with no cached hero images
   *  - that class resets the default slider CSS animation so it won't be executed when `.img-in` class is added when `img.complete` is detected
   *  - gets the first slide's `.lazy-image` container and listens for `transitionend` event of its `<img>` child element
   *  - adds a class modifier after `<img>` transition has completed, when shimmer effect has been removed, that should trigger the hero image animation
   *  - removes classes on Theme Editor `shopify:section:unload` and `shopify:section:reorder` events
   */
  handleFirstSlideAnimation(onLoad = false) {
    if (!onLoad) {
      this.container.classList.remove(c.classes$3.sliderNoCachedImages);
      this.container.classList.remove(c.classes$3.sliderImgLoaded);
      return;
    }

    const firstSlide = this.container.querySelectorAll(s.selectors$3.item)[0];
    const slideImage = firstSlide.querySelectorAll(s.selectors$3.lazyImage);
    const slideImgComplete = this.container.classList.contains(c.classes$3.imgIn);

    if (slideImage.length && !slideImgComplete) {
      this.container.classList.add(c.classes$3.sliderNoCachedImages);

      const onImageTransitionEnd = (event) => {
        requestAnimationFrame(() => this.container.classList.add(c.classes$3.sliderImgLoaded));
        slideImage[0].removeEventListener('transitionend', onImageTransitionEnd);
      };

      slideImage[0].addEventListener('transitionend', onImageTransitionEnd);
    }
  }

  // Scroll down function
  bindScrollButton() {
    const arrowDown = this.container.querySelector(s.selectors$3.arrowScrollDown);

    if (arrowDown) {
      arrowDown.addEventListener('click', (e) => {
        e.preventDefault();

        const headerHeight = this.header.classList.contains(c.classes$3.headerFixed) ? 60 : 0;
        const scrollToPosition = parseInt(
          Math.ceil(this.container.offsetTop + this.container.offsetHeight - headerHeight)
        );

        window.scrollTo({
          top: scrollToPosition,
          left: 0,
          behavior: 'smooth',
        });
      });
    }
  }

  onBlockSelect(evt) {
    const index = parseInt([...evt.target.parentNode.children].indexOf(evt.target));

    if (this.flkty !== null) {
      this.flkty.select(index);
      this.flkty.pausePlayer();
    }
  }

  onBlockDeselect(evt) {
    const autoplay = evt.target.closest(s.selectors$3.slider).dataset.autoplay === 'true';
    if (autoplay && this.flkty !== null) {
      this.flkty.playPlayer();
    }
  }

  onReorder() {
    this.handleFirstSlideAnimation(false);

    if (this.flkty !== null) {
      this.flkty.resize();
    }
  }

  onUnload() {
    this.handleFirstSlideAnimation(false);

    if (this.flkty !== null) {
      document.removeEventListener('theme:vars', this.resizeEvent);
      this.flkty.destroy();
      this.flkty = null;
    }
  }
}